import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import Icon from '../../components/Icon';
import { getUsers, ALLOWED_ROLES } from '../../api';
import { getCurrentUserIdentity, initialsOf, isSelf } from '../chatUtils';
import { GROUP_MAX_MEMBERS, GROUP_MIN_MEMBERS, GROUP_TITLE_MAX_LENGTH } from '../chatConstants';

// Писать можно сотрудникам: от саппорта до дева.
const STAFF_ROLES = [
  ALLOWED_ROLES.SUPPORT,
  ALLOWED_ROLES.MODERATOR,
  ALLOWED_ROLES.ADMIN,
  ALLOWED_ROLES.DEV,
];

/**
 * Выбор людей из /api/support/users. Режимы:
 *  - direct: один клик по человеку → личный чат;
 *  - group:  название + мультивыбор, создаём группу;
 *  - add:    мультивыбор, добавляем в существующую группу.
 * Бэкенд фильтрует по одной роли за запрос, поэтому «Все роли» —
 * это параллельные запросы по каждой роли с последующим слиянием.
 */
const PeoplePickerDialog = ({ open, mode = 'direct', excludeIds = [], onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const multi = mode !== 'direct';
  const texts = {
    title: t(`chat.picker.${mode === 'group' ? 'group' : mode === 'add' ? 'add' : 'direct'}Title`),
    subtitle: t(`chat.picker.${mode === 'group' ? 'group' : mode === 'add' ? 'add' : 'direct'}Sub`),
  };
  // Себя убираем из результатов всех четырёх ролевых запросов: бэкенд
  // отвечает 400 «Chat with yourself is not allowed».
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  // Ключ-строка вместо самого массива: без неё новый массив на каждом рендере
  // пересоздавал search, эффект перезапускался и список грузился по кругу.
  const excludeKey = (excludeIds || []).map(String).sort().join(',');
  const excluded = useMemo(
    () => new Set(excludeKey ? excludeKey.split(',') : []),
    [excludeKey]
  );

  const search = useCallback(async (name, selectedRole) => {
    const requestId = (requestIdRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      const roles = selectedRole ? [selectedRole] : STAFF_ROLES;
      const pages = await Promise.all(
        roles.map((r) => getUsers(0, 20, 'fullName', name, '', r).catch(() => ({ content: [] })))
      );
      if (requestId !== requestIdRef.current) return;

      const merged = [];
      const seen = new Set();
      pages.forEach((page) => {
        (page?.content || []).forEach((user) => {
          const id = user.id || user.userId || user.userProfileId;
          if (!id || seen.has(id) || isSelf(user, identity) || excluded.has(String(id))) return;
          seen.add(id);
          merged.push({ ...user, id });
        });
      });
      merged.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
      setUsers(merged);
    } catch {
      if (requestId === requestIdRef.current) setError(t('chat.picker.loadError'));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [identity, excluded, t]);

  // Дебаунс поиска по имени.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => search(query.trim(), role), 300);
    return () => clearTimeout(timer);
  }, [open, query, role, search]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setRole('');
      setUsers([]);
      setSelected([]);
      setTitle('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const toggle = (user) => {
    setSelected((prev) => {
      if (prev.some((u) => u.id === user.id)) return prev.filter((u) => u.id !== user.id);
      if (prev.length >= GROUP_MAX_MEMBERS) return prev;
      return [...prev, user];
    });
  };

  const submit = async (payload) => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(payload);
      onClose();
    } catch (e) {
      setError(e?.message || t('chat.picker.actionError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (user) => {
    if (multi) {
      toggle(user);
      return;
    }
    submit(user);
  };

  // Минимум 3 участника в группе, создатель считается автоматически.
  const canCreateGroup = title.trim().length > 0 && selected.length >= GROUP_MIN_MEMBERS - 1;
  const canSubmit = mode === 'group' ? canCreateGroup : selected.length > 0;

  return createPortal(
    <div className="chat-modal-backdrop" onMouseDown={onClose}>
      <div className="chat-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="chat-modal-head">
          <div>
            <h3 className="chat-modal-title">{texts.title}</h3>
            <p className="chat-modal-subtitle">{texts.subtitle}</p>
          </div>
          <button type="button" className="chat-icon-btn" onClick={onClose} title={t('calendar.dialog.close')}>
            <Icon name="x" size={18} />
          </button>
        </header>

        {mode === 'group' && (
          <div className="chat-modal-field">
            <input
              className="chat-modal-input"
              value={title}
              maxLength={GROUP_TITLE_MAX_LENGTH}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('chat.picker.namePlaceholder')}
            />
            <span className="chat-modal-counter">{title.length}/{GROUP_TITLE_MAX_LENGTH}</span>
          </div>
        )}

        <div className="chat-search chat-modal-search">
          <Icon name="search" size={16} />
          <input
            autoFocus={mode !== 'group'}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('chat.picker.searchPlaceholder')}
          />
        </div>

        <div className="chat-role-chips">
          <button
            type="button"
            className={`chat-role-chip${role === '' ? ' active' : ''}`}
            onClick={() => setRole('')}
          >
            {t('chat.picker.allRoles')}
          </button>
          {STAFF_ROLES.map((item) => (
            <button
              key={item}
              type="button"
              className={`chat-role-chip${role === item ? ' active' : ''}`}
              onClick={() => setRole(item)}
            >
              {t(`chat.picker.roles.${item}`, item)}
            </button>
          ))}
        </div>

        {multi && selected.length > 0 && (
          <div className="chat-selected-chips">
            {selected.map((user) => (
              <button key={user.id} type="button" className="chat-selected-chip" onClick={() => toggle(user)}>
                {user.fullName || user.username}
                <Icon name="x" size={12} />
              </button>
            ))}
          </div>
        )}

        {error && <div className="chat-composer-error">{error}</div>}

        <div className="chat-modal-list">
          {loading && (
            <div className="chat-list-empty">
              <div className="strict-loader" />
            </div>
          )}

          {!loading && users.length === 0 && (
            <div className="chat-list-empty">
              <Icon name="users" size={28} />
              <p>{t('chat.picker.notFound')}</p>
            </div>
          )}

          {!loading && users.map((user) => {
            const checked = selected.some((u) => u.id === user.id);
            return (
              <button
                key={user.id}
                type="button"
                className={`chat-modal-item${checked ? ' selected' : ''}`}
                onClick={() => handleRowClick(user)}
                disabled={submitting}
              >
                <span className="chat-avatar">{initialsOf(user.fullName || user.username) || '?'}</span>
                <span className="chat-modal-item-main">
                  <span className="chat-list-name">{user.fullName || user.username || user.phoneNumber}</span>
                  <span className="chat-list-preview">
                    {t(`chat.picker.roles.${user.role}`, user.role)}
                    {user.phoneNumber ? ` · ${user.phoneNumber}` : ''}
                  </span>
                </span>
                {multi
                  ? <span className={`chat-check-box${checked ? ' checked' : ''}`}>{checked && <Icon name="check" size={12} />}</span>
                  : <Icon name="chevron-right" size={16} />}
              </button>
            );
          })}
        </div>

        {multi && (
          <footer className="chat-modal-foot">
            <span className="chat-modal-hint">
              {mode === 'group'
                ? t('chat.picker.selectedMin', { count: selected.length, min: GROUP_MIN_MEMBERS - 1 })
                : t('chat.picker.selected', { count: selected.length })}
            </span>
            <button
              type="button"
              className="chat-modal-submit"
              disabled={!canSubmit || submitting}
              onClick={() => submit(
                mode === 'group'
                  ? { title: title.trim(), memberIds: selected.map((u) => u.id) }
                  : { memberIds: selected.map((u) => u.id) }
              )}
            >
              {submitting
                ? t('chat.picker.saving')
                : mode === 'group' ? t('chat.picker.create') : t('chat.picker.add')}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PeoplePickerDialog;
